import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { services, galleryImages } from '../mockData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { X, ImageOff } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

// Lazy loaded image component with loading state and error handling
const LazyImage = ({ src, alt, className, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className="relative w-full h-full">
      {!loaded && !error && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {error ? (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <ImageOff className="w-12 h-12 text-gray-400" />
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={`${className} ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          onClick={onClick}
        />
      )}
    </div>
  );
};

const Gallery = () => {
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  // Scroll to top and set category from URL params on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    if (category && (category === 'all' || galleryImages[category])) {
      setSelectedCategory(category);
    }
  }, [location.search]);

  const getAllImages = () => {
    const allImages = [];
    Object.keys(galleryImages).forEach((category) => {
      galleryImages[category].forEach((img) => {
        allImages.push({ ...img, category });
      });
    });
    return allImages;
  };

  const getFilteredImages = () => {
    if (selectedCategory === 'all') {
      return getAllImages();
    }
    return galleryImages[selectedCategory]?.map((img) => ({
      ...img,
      category: selectedCategory
    })) || [];
  };

  const getCategoryName = (categoryId) => {
    const service = services.find((s) => s.id === categoryId);
    return service?.name || categoryId;
  };

  const filteredImages = getFilteredImages();

  // Handle category change with scroll to top
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Project Gallery</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Explore our portfolio of completed projects and see the quality craftsmanship we deliver
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white shadow-md py-6 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => handleCategoryChange('all')}
              className={selectedCategory === 'all' ? 'bg-red-600 hover:bg-red-700' : 'hover:border-red-600 hover:text-red-600'}
            >
              All Projects
            </Button>
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <Button
                  key={service.id}
                  variant={selectedCategory === service.id ? 'default' : 'outline'}
                  onClick={() => handleCategoryChange(service.id)}
                  className={selectedCategory === service.id ? 'bg-red-600 hover:bg-red-700' : 'hover:border-red-600 hover:text-red-600'}
                >
                  {IconComponent && <IconComponent size={16} className="mr-2" />}
                  {service.name}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          {filteredImages.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-gray-600">No images found for this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredImages.map((image) => {
                const isVideo = image.url.endsWith('.mp4') || image.url.endsWith('.mov') || image.url.endsWith('.webm');
                return (
                  <Card
                    key={`${image.category}-${image.id}`}
                    className="group cursor-pointer overflow-hidden hover:shadow-xl transition-all duration-300 border-2 hover:border-red-200"
                    onClick={() => setSelectedImage(image)}
                  >
                    <div className="relative aspect-square overflow-hidden bg-gray-100">
                      {isVideo ? (
                        <video
                          src={image.url}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          muted
                          loop
                          playsInline
                          preload="metadata"
                          onMouseEnter={(e) => e.target.play()}
                          onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                        />
                      ) : (
                        <LazyImage
                          src={image.url}
                          alt={image.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <p className="text-white font-semibold text-sm">{image.title}</p>
                          <p className="text-gray-300 text-xs mt-1">{getCategoryName(image.category)}</p>
                        </div>
                      </div>
                      {isVideo && (
                        <div className="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs font-medium">
                          VIDEO
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Image/Video Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-red-500 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          <div className="max-w-5xl w-full">
            {(selectedImage.url.endsWith('.mp4') || selectedImage.url.endsWith('.mov') || selectedImage.url.endsWith('.webm')) ? (
              <video
                src={selectedImage.url}
                className="w-full h-auto rounded-lg shadow-2xl max-h-[80vh]"
                controls
                autoPlay
                loop
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full h-auto rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <div className="text-center mt-6">
              <h3 className="text-white text-2xl font-bold mb-2">{selectedImage.title}</h3>
              <p className="text-gray-300 text-lg">{getCategoryName(selectedImage.category)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;